import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ShieldAlert, Send, Loader2, CheckCircle2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { CocoonMark } from "@/components/CocoonMark";

const T = {
  fr: {
    title: "Signaler un contenu", subtitle: "Notice & Action — conformément à l'article 16 du DSA.",
    intro: "Tu penses qu'un contenu publié sur COCOON est illicite ? Décris-le ci-dessous : nous l'examinerons avec diligence et objectivité, et t'informerons de la décision prise.",
    category: "Type de contenu", location: "Où se trouve ce contenu ?",
    locationPh: "Espace concerné (Feu de camp, L'Arène…), lien, date et heure si possible",
    description: "Pourquoi ce contenu te semble-t-il illicite ?",
    descriptionPh: "Décris précisément le problème…",
    email: "Ton e-mail (facultatif)", emailPh: "pour te tenir informé·e du suivi",
    emailNote: "Facultatif — et non requis si le signalement concerne des mineurs.",
    submit: "Envoyer le signalement", sending: "Envoi…",
    ok: "Signalement envoyé. Merci — nous l'examinons.", err: "L'envoi a échoué. Réessaie.",
    doneTitle: "Merci pour ta vigilance", doneBody: "Ton signalement a bien été transmis à notre équipe. Nous l'examinons dans les meilleurs délais et pourrons te contacter si tu as laissé ton e-mail.",
    back: "← Retour", another: "Faire un autre signalement",
    cats: { haine: "Propos haineux", harcelement: "Harcèlement", violence: "Violence", sexuel_mineurs: "Contenu impliquant des mineurs", desinformation: "Désinformation", propriete: "Atteinte à la propriété intellectuelle", autre: "Autre" },
  },
  en: {
    title: "Report content", subtitle: "Notice & Action — under Article 16 of the DSA.",
    intro: "Do you believe a piece of content on COCOON is illegal? Describe it below: we'll review it diligently and objectively, and inform you of the decision.",
    category: "Content type", location: "Where is this content?",
    locationPh: "Space (Campfire, The Arena…), link, date and time if possible",
    description: "Why do you consider this content illegal?",
    descriptionPh: "Describe the problem precisely…",
    email: "Your email (optional)", emailPh: "to keep you informed",
    emailNote: "Optional — and not required if the report concerns minors.",
    submit: "Send report", sending: "Sending…",
    ok: "Report sent. Thank you — we're reviewing it.", err: "Sending failed. Try again.",
    doneTitle: "Thank you for your vigilance", doneBody: "Your report has been sent to our team. We'll review it as soon as possible and may contact you if you left your email.",
    back: "← Back", another: "Submit another report",
    cats: { haine: "Hate speech", harcelement: "Harassment", violence: "Violence", sexuel_mineurs: "Content involving minors", desinformation: "Disinformation", propriete: "Intellectual property infringement", autre: "Other" },
  },
  es: {
    title: "Denunciar contenido", subtitle: "Notice & Action — conforme al artículo 16 del DSA.",
    intro: "¿Crees que un contenido publicado en COCOON es ilícito? Descríbelo a continuación: lo revisaremos con diligencia y objetividad, y te informaremos de la decisión.",
    category: "Tipo de contenido", location: "¿Dónde está este contenido?",
    locationPh: "Espacio (Hoguera, La Arena…), enlace, fecha y hora si es posible",
    description: "¿Por qué consideras que este contenido es ilícito?",
    descriptionPh: "Describe el problema con precisión…",
    email: "Tu correo (opcional)", emailPh: "para mantenerte informado",
    emailNote: "Opcional — y no obligatorio si la denuncia afecta a menores.",
    submit: "Enviar la denuncia", sending: "Enviando…",
    ok: "Denuncia enviada. Gracias — la estamos revisando.", err: "El envío falló. Inténtalo de nuevo.",
    doneTitle: "Gracias por tu vigilancia", doneBody: "Tu denuncia se ha enviado a nuestro equipo. La revisaremos lo antes posible y podremos contactarte si dejaste tu correo.",
    back: "← Volver", another: "Hacer otra denuncia",
    cats: { haine: "Discurso de odio", harcelement: "Acoso", violence: "Violencia", sexuel_mineurs: "Contenido que implica a menores", desinformation: "Desinformación", propriete: "Violación de propiedad intelectual", autre: "Otro" },
  },
  nl: {
    title: "Inhoud melden", subtitle: "Notice & Action — conform artikel 16 van de DSA.",
    intro: "Denk je dat inhoud op COCOON onwettig is? Beschrijf het hieronder: we beoordelen het zorgvuldig en objectief en informeren je over de beslissing.",
    category: "Type inhoud", location: "Waar staat deze inhoud?",
    locationPh: "Ruimte (Kampvuur, De Arena…), link, datum en tijd indien mogelijk",
    description: "Waarom vind je deze inhoud onwettig?",
    descriptionPh: "Beschrijf het probleem nauwkeurig…",
    email: "Je e-mail (optioneel)", emailPh: "om je op de hoogte te houden",
    emailNote: "Optioneel — en niet vereist als de melding minderjarigen betreft.",
    submit: "Melding versturen", sending: "Versturen…",
    ok: "Melding verzonden. Bedankt — we bekijken het.", err: "Verzenden mislukt. Probeer opnieuw.",
    doneTitle: "Bedankt voor je waakzaamheid", doneBody: "Je melding is naar ons team gestuurd. We bekijken het zo snel mogelijk en nemen mogelijk contact op als je je e-mail hebt achtergelaten.",
    back: "← Terug", another: "Nog een melding doen",
    cats: { haine: "Haatzaaien", harcelement: "Intimidatie", violence: "Geweld", sexuel_mineurs: "Inhoud met minderjarigen", desinformation: "Desinformatie", propriete: "Inbreuk op intellectueel eigendom", autre: "Anders" },
  },
};

export default function Report() {
  const { i18n } = useTranslation();
  const L = T[(i18n.language || "fr").slice(0, 2)] || T.fr;
  const [category, setCategory] = useState("haine");
  const [location, setLocation] = useState("");
  const [description, setDescription] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/report", {
        category, location, description,
        reporter_email: email.trim() || null,
      });
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || L.err);
    } finally { setLoading(false); }
  };

  const reset = () => { setDone(false); setLocation(""); setDescription(""); setEmail(""); setCategory("haine"); };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="fixed top-0 inset-x-0 z-50 glass">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="font-display text-2xl tracking-tight"><CocoonMark withMind /></Link>
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            <Link to="/" className="text-sm text-foreground/60 hover:text-primary transition-colors">{L.back}</Link>
          </div>
        </div>
      </header>

      <main className="max-w-xl mx-auto px-6 pt-32 pb-24">
        <div className="flex justify-center mb-4"><span className="w-12 h-12 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary"><ShieldAlert size={22} /></span></div>
        <h1 className="font-display text-gradient-orange text-center text-4xl tracking-tight mb-2" data-testid="report-title">{L.title}</h1>
        <p className="font-display italic text-primary/80 text-center mb-8">{L.subtitle}</p>

        {done ? (
          <div className="glass rounded-2xl border border-primary/20 p-8 text-center frame-pulse" data-testid="report-done">
            <CheckCircle2 size={40} className="text-primary mx-auto mb-4" />
            <h2 className="font-display text-2xl text-foreground mb-2">{L.doneTitle}</h2>
            <p className="text-foreground/60 leading-relaxed mb-6">{L.doneBody}</p>
            <button onClick={reset} data-testid="report-another-btn" className="text-sm text-primary hover:text-primary/80 underline underline-offset-4">{L.another}</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5" data-testid="report-form">
            <p className="text-sm text-foreground/60 leading-relaxed">{L.intro}</p>

            <div>
              <label className="block text-xs uppercase tracking-wider text-foreground/40 mb-2">{L.category}</label>
              <select data-testid="report-category" value={category} onChange={(e) => setCategory(e.target.value)}
                className="w-full bg-black/30 border border-white/15 rounded-xl px-4 py-3 text-sm text-foreground/85 outline-none focus:border-primary/40">
                {Object.entries(L.cats).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-xs uppercase tracking-wider text-foreground/40 mb-2">{L.location}</label>
              <input data-testid="report-location" value={location} onChange={(e) => setLocation(e.target.value)} placeholder={L.locationPh}
                className="w-full bg-black/30 border border-white/15 rounded-xl px-4 py-3 text-sm text-foreground/85 outline-none focus:border-primary/40 placeholder:text-foreground/30" />
            </div>

            <div>
              <label className="block text-xs uppercase tracking-wider text-foreground/40 mb-2">{L.description}</label>
              <textarea data-testid="report-description" value={description} onChange={(e) => setDescription(e.target.value)} placeholder={L.descriptionPh} rows={5} required
                className="w-full bg-black/30 border border-white/15 rounded-xl px-4 py-3 text-sm text-foreground/85 outline-none focus:border-primary/40 placeholder:text-foreground/30 resize-none" />
            </div>

            <div>
              <label className="block text-xs uppercase tracking-wider text-foreground/40 mb-2">{L.email}</label>
              <input data-testid="report-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={L.emailPh}
                className="w-full bg-black/30 border border-white/15 rounded-xl px-4 py-3 text-sm text-foreground/85 outline-none focus:border-primary/40 placeholder:text-foreground/30" />
              <p className="text-xs text-foreground/35 mt-1.5">{L.emailNote}</p>
            </div>

            <button type="submit" data-testid="report-submit-btn" disabled={loading || description.trim().length < 10}
              className="btn-frost-orange w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full font-semibold text-sm disabled:opacity-50">
              {loading ? <Loader2 size={17} className="animate-spin" /> : <Send size={16} />} {loading ? L.sending : L.submit}
            </button>
          </form>
        )}
      </main>
    </div>
  );
}
